/**
 * Device ID Management Utility for KDS
 * Generates and persists unique device identifiers to prevent duplicate events
 * across multiple devices viewing the same production center
 */

const DEVICE_ID_KEY = "kds_device_id";

// Cache for the async Electron device ID
let electronDeviceIdPromise = null;

/**
 * Generate a UUID v4 format device ID
 * @returns {string} UUID format device ID
 */
function generateDeviceId() {
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get hardware-anchored device ID from Electron main process (async).
 * Returns null if not in Electron mode.
 * @returns {Promise<string|null>}
 */
export async function getElectronDeviceId() {
  if (typeof window !== "undefined" && window.electronAPI?.device) {
    if (!electronDeviceIdPromise) {
      electronDeviceIdPromise = window.electronAPI.device.getId();
    }
    return electronDeviceIdPromise;
  }
  return null;
}

/**
 * Get existing device ID from localStorage or create a new one.
 * In Electron mode, prefer the hardware-anchored ID (call getElectronDeviceId() for async version).
 * This synchronous version falls back to localStorage for browser mode.
 * @returns {string} Device ID
 */
export function getOrCreateDeviceId() {
  // Handle SSR/Node environment
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    console.warn(
      "[KDS] localStorage not available, generating temporary device ID"
    );
    return generateDeviceId();
  }

  try {
    // Try to get existing device ID
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);

    if (deviceId) {
      return deviceId;
    }

    // Generate new device ID if none exists
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);

    return deviceId;
  } catch (error) {
    console.error("[KDS] Error accessing localStorage for device ID:", error);
    return generateDeviceId();
  }
}

/**
 * Manually set a custom device ID (useful for pre-labeled tablets)
 * Example: setDeviceId('kitchen-tablet-1')
 * @param {string} customId - Custom device identifier
 */
export function setDeviceId(customId) {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    console.warn("[KDS] localStorage not available, cannot set device ID");
    return;
  }

  try {
    localStorage.setItem(DEVICE_ID_KEY, customId);
  } catch (error) {
    console.error("[KDS] Error setting device ID:", error);
  }
}

